import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { HexColorPicker } from 'react-colorful';
import { StableInput, StableTextarea } from '../ui/StableInput';
import { TextFormattingPanel, TextStyle } from '../ui/TextFormattingPanel';
import IsolatedInput from '../ui/IsolatedInput';
import { AdvancedDataTableControls } from './AdvancedBlocks';
import {
  Type,
  Palette,
  Move,
  Square,
  Trash2,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Code,
  Settings,
  Layers
} from 'lucide-react';

// ─── PropertySection ──────────────────────────────────────────────────────────
// Defined OUTSIDE PropertyPanel so it has a stable component identity across
// re-renders. If it were defined inside, React would unmount/remount all
// children (inputs) on every re-render, causing focus loss after each keystroke.

type PropertySectionProps = {
  title: string;
  icon: React.ElementType;
  sectionKey: string;
  expandedSections: Set<string>;
  onToggle: (key: string) => void;
  children: React.ReactNode;
};

const PropertySection = React.memo(({
  title,
  icon: Icon,
  sectionKey,
  expandedSections,
  onToggle,
  children,
}: PropertySectionProps) => {
  const isExpanded = expandedSections.has(sectionKey);
  return (
    <div className="mb-4">
      <button
        onClick={() => onToggle(sectionKey)}
        className="w-full flex items-center justify-between p-2 hover:bg-gray-50 rounded"
      >
        <div className="flex items-center">
          <Icon className="w-4 h-4 mr-2 text-gray-600" />
          <span className="text-sm font-medium text-gray-900">{title}</span>
        </div>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
      </button>
      {isExpanded && (
        <div className="pl-6 pr-2 pb-2 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
});

PropertySection.displayName = 'PropertySection';

// High-performance Script Editor with aggressive anti-bounce protection
const ScriptEditor: React.FC<{
  blockId: string;
  content: { html: string; css: string; javascript: string };
  onUpdate: (field: string, value: string) => void;
}> = React.memo(({ blockId, content, onUpdate }) => {
  // Local state to prevent external re-renders from affecting editing
  const [localContent, setLocalContent] = useState({
    html: content.html || '',
    css: content.css || '',
    javascript: content.javascript || ''
  });

  // Debounced update refs
  const updateTimeoutRef = useRef<NodeJS.Timeout>();
  const isUpdatingRef = useRef(false);

  // Sync external changes to local state only when not actively editing
  useEffect(() => {
    if (!isUpdatingRef.current) {
      setLocalContent({
        html: content.html || '',
        css: content.css || '',
        javascript: content.javascript || ''
      });
    }
  }, [content.html, content.css, content.javascript]);

  // Debounced update function
  const debouncedUpdate = useCallback((field: string, value: string) => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    isUpdatingRef.current = true;

    updateTimeoutRef.current = setTimeout(() => {
      onUpdate(field, value);
      isUpdatingRef.current = false;
    }, 300); // 300ms debounce
  }, [onUpdate]);

  // Stable change handlers with local state management
  const handleHtmlChange = useCallback((value: string) => {
    setLocalContent(prev => ({ ...prev, html: value }));
    debouncedUpdate('content.html', value);
  }, [debouncedUpdate]);

  const handleCssChange = useCallback((value: string) => {
    setLocalContent(prev => ({ ...prev, css: value }));
    debouncedUpdate('content.css', value);
  }, [debouncedUpdate]);

  const handleJsChange = useCallback((value: string) => {
    setLocalContent(prev => ({ ...prev, javascript: value }));
    debouncedUpdate('content.javascript', value);
  }, [debouncedUpdate]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">HTML</label>
        <StableTextarea
          key={`script-html-${blockId}`}
          value={localContent.html}
          onChange={handleHtmlChange}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none font-mono"
          rows={6}
          placeholder="Enter your HTML markup here..."
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">CSS</label>
        <StableTextarea
          key={`script-css-${blockId}`}
          value={localContent.css}
          onChange={handleCssChange}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none font-mono"
          rows={6}
          placeholder="/* Add your CSS styles here */"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">JavaScript</label>
        <StableTextarea
          key={`script-js-${blockId}`}
          value={localContent.javascript}
          onChange={handleJsChange}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none font-mono"
          rows={6}
          placeholder="// Add your JavaScript code here"
        />
      </div>
    </>
  );
});

ScriptEditor.displayName = 'ScriptEditor';

interface EditorBlock {
  id: string;
  type: 'text' | 'image' | 'shape' | 'table' | 'placeholder' | 'chart' | 'video' | 'button' | 'divider' | 'icon' | 'qrcode' | 'signature' | 'datetime' | 'progress' | 'list' | 'bar-chart' | 'line-chart' | 'pie-chart' | 'kpi-card' | 'progress-bar' | 'gauge-chart' | 'data-table' | 'badge' | 'quote' | 'callout' | 'gradient-bg' | 'border-frame' | 'timeline' | 'accordion' | 'icon-block' | 'heading1' | 'heading2' | 'heading3' | 'paragraph' | 'variable' | 'script';
  position: { x: number; y: number };
  size: { width: number; height: number };
  content: any;
  style: any;
  textStyle?: TextStyle; // New text styling properties
  zIndex: number;
  visible?: boolean;
  locked?: boolean;
}

interface PropertyPanelProps {
  selectedBlock: EditorBlock | null;
  onUpdateBlock: (blockId: string, updates: Partial<EditorBlock>) => void;
  onDeleteBlock: (blockId: string) => void;
}

const PropertyPanel: React.FC<PropertyPanelProps> = React.memo(({
  selectedBlock,
  onUpdateBlock,
  onDeleteBlock,
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['position', 'style'])
  );
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showAdvancedDataTableControls, setShowAdvancedDataTableControls] = useState(false);

  // Always-current ref so updateBlockProperty never needs to close over selectedBlock.
  // This prevents the callback from being recreated on every keystroke, which was
  // causing StableInput's debouncedOnChange to be rebuilt mid-typing (cursor bounce).
  const selectedBlockRef = useRef(selectedBlock);
  selectedBlockRef.current = selectedBlock;

  const toggleSection = useCallback((section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  }, []);

  // Stable callback — only recreated when onUpdateBlock changes (almost never).
  const updateBlockProperty = useCallback((path: string, value: any) => {
    const block = selectedBlockRef.current;
    if (!block) return;

    const pathParts = path.split('.');
    const updates: any = {};

    if (pathParts.length === 1) {
      updates[pathParts[0]] = value;
    } else if (pathParts.length === 2) {
      updates[pathParts[0]] = {
        ...block[pathParts[0] as keyof EditorBlock],
        [pathParts[1]]: value,
      };
    }

    onUpdateBlock(block.id, updates);
  }, [onUpdateBlock]); // no longer depends on selectedBlock

  if (!selectedBlock) {
    return (
      <div className="h-full bg-white border-l border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Properties</h3>
        <div className="text-center text-gray-500 mt-8">
          <Square className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-sm">Select a block to edit properties</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-white border-l border-gray-200 p-4 overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Properties</h3>
        <div className="flex items-center space-x-1">
          <button
            onClick={() => updateBlockProperty('visible', !selectedBlock.visible)}
            className="p-1 hover:bg-gray-100 rounded"
            title={selectedBlock.visible ? 'Hide' : 'Show'}
          >
            {selectedBlock.visible !== false ? (
              <Eye className="w-4 h-4 text-gray-600" />
            ) : (
              <EyeOff className="w-4 h-4 text-gray-400" />
            )}
          </button>
          <button
            onClick={() => updateBlockProperty('locked', !selectedBlock.locked)}
            className="p-1 hover:bg-gray-100 rounded"
            title={selectedBlock.locked ? 'Unlock' : 'Lock'}
          >
            {selectedBlock.locked ? (
              <Lock className="w-4 h-4 text-gray-600" />
            ) : (
              <Unlock className="w-4 h-4 text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* Block Info */}
      <div className="mb-6 p-3 bg-gray-50 rounded-lg">
        <div className="text-xs text-gray-500 mb-1">Block Type</div>
        <div className="text-sm font-medium capitalize">{selectedBlock.type}</div>
        <div className="text-xs text-gray-500 mt-2 mb-1">ID</div>
        <div className="text-xs font-mono text-gray-600 break-all">
          {selectedBlock.id}
        </div>
      </div>

      {/* Position & Size */}
      <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Position & Size" icon={Move} sectionKey="position">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">X</label>
            <input
              type="number"
              value={Math.round(selectedBlock.position.x)}
              onChange={(e) => updateBlockProperty('position.x', parseInt(e.target.value) || 0)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Y</label>
            <input
              type="number"
              value={Math.round(selectedBlock.position.y)}
              onChange={(e) => updateBlockProperty('position.y', parseInt(e.target.value) || 0)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Width</label>
            <input
              type="number"
              value={Math.round(selectedBlock.size.width)}
              onChange={(e) => updateBlockProperty('size.width', parseInt(e.target.value) || 0)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Height</label>
            <input
              type="number"
              value={Math.round(selectedBlock.size.height)}
              onChange={(e) => updateBlockProperty('size.height', parseInt(e.target.value) || 0)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            />
          </div>
        </div>
      </PropertySection>

      {/* Style Properties */}
      <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Style" icon={Palette} sectionKey="style">


        {selectedBlock.type === 'shape' && (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Shape</label>
              <select
                value={selectedBlock.content.shapeType || 'rectangle'}
                onChange={(e) => updateBlockProperty('content.shapeType', e.target.value)}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              >
                <optgroup label="Basic Shapes">
                  <option value="rectangle">Rectangle</option>
                  <option value="circle">Circle</option>
                  <option value="triangle">Triangle</option>
                  <option value="line">Line</option>
                </optgroup>
                <optgroup label="Advanced Shapes">
                  <option value="arrow-right">Arrow Right</option>
                  <option value="arrow-left">Arrow Left</option>
                  <option value="arrow-up">Arrow Up</option>
                  <option value="arrow-down">Arrow Down</option>
                  <option value="star">Star</option>
                  <option value="diamond">Diamond</option>
                  <option value="hexagon">Hexagon</option>
                  <option value="heart">Heart</option>
                  <option value="cross">Cross</option>
                </optgroup>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Fill Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={selectedBlock.content.fillColor || '#3b82f6'}
                  onChange={(e) => updateBlockProperty('content.fillColor', e.target.value)}
                  className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={selectedBlock.content.fillColor || '#3b82f6'}
                  onChange={(e) => updateBlockProperty('content.fillColor', e.target.value)}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs font-mono"
                  placeholder="#3b82f6"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Stroke Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={selectedBlock.content.strokeColor || '#1e40af'}
                  onChange={(e) => updateBlockProperty('content.strokeColor', e.target.value)}
                  className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={selectedBlock.content.strokeColor || '#1e40af'}
                  onChange={(e) => updateBlockProperty('content.strokeColor', e.target.value)}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs font-mono"
                  placeholder="#1e40af"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Stroke Width</label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={selectedBlock.content.strokeWidth || 0}
                  onChange={(e) => updateBlockProperty('content.strokeWidth', parseInt(e.target.value))}
                  className="flex-1"
                />
                <input
                  type="number"
                  min="0"
                  max="20"
                  value={selectedBlock.content.strokeWidth || 0}
                  onChange={(e) => updateBlockProperty('content.strokeWidth', parseInt(e.target.value) || 0)}
                  className="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                />
              </div>
            </div>
          </>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Z-Index</label>
          <input
            type="number"
            value={selectedBlock.zIndex}
            onChange={(e) => updateBlockProperty('zIndex', parseInt(e.target.value) || 1)}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
          />
        </div>
      </PropertySection>

      {/* Content Properties */}
      {selectedBlock.type === 'text' && (
        <>
          <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Content" icon={Type} sectionKey="content">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Text</label>
              <StableTextarea
                value={selectedBlock.content.text || ''}
                onChange={(value) => updateBlockProperty('content.text', value)}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none"
                rows={3}
                placeholder="Enter text content..."
              />
            </div>
          </PropertySection>

          <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Text Formatting" icon={Type} sectionKey="textFormatting">
            <TextFormattingPanel
              textStyle={selectedBlock.textStyle || {}}
              onChange={(textStyle) => updateBlockProperty('textStyle', textStyle)}
              className="border-0 shadow-none bg-transparent p-0"
            />
          </PropertySection>
        </>
      )}

      {/* Heading Blocks */}
      {(selectedBlock.type === 'heading1' || selectedBlock.type === 'heading2' || selectedBlock.type === 'heading3' || selectedBlock.type === 'paragraph') && (
        <>
          <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Content" icon={Type} sectionKey="content">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {selectedBlock.type === 'heading1' ? 'Heading 1' :
                 selectedBlock.type === 'heading2' ? 'Heading 2' :
                 selectedBlock.type === 'heading3' ? 'Heading 3' : 'Paragraph'}
              </label>
              <StableTextarea
                value={selectedBlock.content.text || ''}
                onChange={(value) => updateBlockProperty('content.text', value)}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none"
                rows={selectedBlock.type === 'paragraph' ? 4 : 2}
                placeholder={
                  selectedBlock.type === 'heading1' ? 'Enter heading 1...' :
                  selectedBlock.type === 'heading2' ? 'Enter heading 2...' :
                  selectedBlock.type === 'heading3' ? 'Enter heading 3...' :
                  'Enter paragraph text...'
                }
              />
            </div>
          </PropertySection>

          <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Text Formatting" icon={Type} sectionKey="textFormatting">
            <TextFormattingPanel
              textStyle={selectedBlock.textStyle || {}}
              onChange={(textStyle) => updateBlockProperty('textStyle', textStyle)}
              className="border-0 shadow-none bg-transparent p-0"
            />
          </PropertySection>
        </>
      )}

      {selectedBlock.type === 'image' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Image" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Image URL</label>
            <input
              type="url"
              value={selectedBlock.content.src || ''}
              onChange={(e) => updateBlockProperty('content.src', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="https://example.com/image.jpg"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Alt Text</label>
            <input
              type="text"
              value={selectedBlock.content.alt || ''}
              onChange={(e) => updateBlockProperty('content.alt', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="Image description"
            />
          </div>
        </PropertySection>
      )}

      {selectedBlock.type === 'button' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Button" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Button Text</label>
            <StableInput
              type="text"
              value={selectedBlock.content.text || ''}
              onChange={(value) => updateBlockProperty('content.text', value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="Click Me"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Link URL</label>
            <StableInput
              type="url"
              value={selectedBlock.content.link || ''}
              onChange={(value) => updateBlockProperty('content.link', value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="https://example.com"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Style</label>
            <select
              value={selectedBlock.content.style || 'primary'}
              onChange={(e) => updateBlockProperty('content.style', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
              <option value="outline">Outline</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Size</label>
            <select
              value={selectedBlock.content.size || 'medium'}
              onChange={(e) => updateBlockProperty('content.size', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </div>
        </PropertySection>
      )}

      {selectedBlock.type === 'video' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Video" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Video URL</label>
            <input
              type="url"
              value={selectedBlock.content.src || ''}
              onChange={(e) => updateBlockProperty('content.src', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="https://example.com/video.mp4"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Poster Image</label>
            <input
              type="url"
              value={selectedBlock.content.poster || ''}
              onChange={(e) => updateBlockProperty('content.poster', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="https://example.com/poster.jpg"
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={selectedBlock.content.autoplay || false}
              onChange={(e) => updateBlockProperty('content.autoplay', e.target.checked)}
              className="mr-2"
            />
            <label className="text-xs text-gray-700">Autoplay</label>
          </div>
        </PropertySection>
      )}

      {selectedBlock.type === 'progress' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Progress" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Value</label>
            <input
              type="number"
              min="0"
              max={selectedBlock.content.max || 100}
              value={selectedBlock.content.value || 0}
              onChange={(e) => updateBlockProperty('content.value', parseInt(e.target.value) || 0)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Maximum</label>
            <input
              type="number"
              min="1"
              value={selectedBlock.content.max || 100}
              onChange={(e) => updateBlockProperty('content.max', parseInt(e.target.value) || 100)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={selectedBlock.content.showLabel !== false}
              onChange={(e) => updateBlockProperty('content.showLabel', e.target.checked)}
              className="mr-2"
            />
            <label className="text-xs text-gray-700">Show Label</label>
          </div>
        </PropertySection>
      )}

      {selectedBlock.type === 'list' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="List" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">List Type</label>
            <select
              value={selectedBlock.content.listType || 'bullet'}
              onChange={(e) => updateBlockProperty('content.listType', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="bullet">Bullet Points</option>
              <option value="numbered">Numbered</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Items (one per line)</label>
            <StableTextarea
              value={(selectedBlock.content.items || []).join('\n')}
              onChange={(value) => updateBlockProperty('content.items', value.split('\n').filter(item => item.trim()))}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none"
              rows={4}
              placeholder="Item 1&#10;Item 2&#10;Item 3"
            />
          </div>
        </PropertySection>
      )}

      {selectedBlock.type === 'qrcode' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="QR Code" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Content</label>
            <StableTextarea
              value={selectedBlock.content.value || ''}
              onChange={(value) => updateBlockProperty('content.value', value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none"
              rows={3}
              placeholder="https://example.com or any text"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Error Correction</label>
            <select
              value={selectedBlock.content.errorLevel || 'M'}
              onChange={(e) => updateBlockProperty('content.errorLevel', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="L">Low (7%)</option>
              <option value="M">Medium (15%)</option>
              <option value="Q">Quartile (25%)</option>
              <option value="H">High (30%)</option>
            </select>
          </div>
        </PropertySection>
      )}

      {selectedBlock.type === 'datetime' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Date/Time" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Format</label>
            <select
              value={selectedBlock.content.format || 'MM/DD/YYYY'}
              onChange={(e) => updateBlockProperty('content.format', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              <option value="MMM DD, YYYY">MMM DD, YYYY</option>
              <option value="MMMM DD, YYYY">MMMM DD, YYYY</option>
            </select>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={selectedBlock.content.showTime || false}
              onChange={(e) => updateBlockProperty('content.showTime', e.target.checked)}
              className="mr-2"
            />
            <label className="text-xs text-gray-700">Show Time</label>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Default Value</label>
            <select
              value={selectedBlock.content.defaultValue || 'current'}
              onChange={(e) => updateBlockProperty('content.defaultValue', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="current">Current Date</option>
              <option value="empty">Empty</option>
              <option value="custom">Custom</option>
            </select>
          </div>
        </PropertySection>
      )}

      {selectedBlock.type === 'signature' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Signature" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Placeholder Text</label>
            <input
              type="text"
              value={selectedBlock.content.placeholder || ''}
              onChange={(e) => updateBlockProperty('content.placeholder', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="Signature"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Style</label>
            <select
              value={selectedBlock.content.style || 'underline'}
              onChange={(e) => updateBlockProperty('content.style', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="underline">Underline</option>
              <option value="box">Box</option>
              <option value="dotted">Dotted Line</option>
            </select>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={selectedBlock.content.required || false}
              onChange={(e) => updateBlockProperty('content.required', e.target.checked)}
              className="mr-2"
            />
            <label className="text-xs text-gray-700">Required</label>
          </div>
        </PropertySection>
      )}

      {selectedBlock.type === 'divider' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Divider" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Orientation</label>
            <select
              value={selectedBlock.content.orientation || 'horizontal'}
              onChange={(e) => updateBlockProperty('content.orientation', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="horizontal">Horizontal</option>
              <option value="vertical">Vertical</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Thickness</label>
            <input
              type="number"
              min="1"
              max="20"
              value={selectedBlock.content.thickness || 2}
              onChange={(e) => updateBlockProperty('content.thickness', parseInt(e.target.value) || 2)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Style</label>
            <select
              value={selectedBlock.content.style || 'solid'}
              onChange={(e) => updateBlockProperty('content.style', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
            </select>
          </div>
        </PropertySection>
      )}

      {selectedBlock.type === 'chart' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Chart" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Chart Type</label>
            <select
              value={selectedBlock.content.chartType || 'bar'}
              onChange={(e) => updateBlockProperty('content.chartType', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="bar">Bar Chart</option>
              <option value="pie">Pie Chart</option>
              <option value="line">Line Chart</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Data (JSON format)</label>
            <StableTextarea
              value={JSON.stringify(selectedBlock.content.data || [], null, 2)}
              onChange={(value) => {
                try {
                  const data = JSON.parse(value);
                  updateBlockProperty('content.data', data);
                } catch (err) {
                  // Invalid JSON, ignore
                }
              }}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none font-mono"
              rows={6}
              placeholder='[{"label": "Jan", "value": 30}]'
            />
          </div>
        </PropertySection>
      )}

      {selectedBlock.type === 'icon' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Icon" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Icon</label>
            <select
              value={selectedBlock.content.iconName || 'star'}
              onChange={(e) => updateBlockProperty('content.iconName', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="star">Star</option>
              <option value="heart">Heart</option>
              <option value="check">Check</option>
              <option value="x">X</option>
              <option value="plus">Plus</option>
              <option value="minus">Minus</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Size</label>
            <input
              type="number"
              min="16"
              max="200"
              value={selectedBlock.content.size || 48}
              onChange={(e) => updateBlockProperty('content.size', parseInt(e.target.value) || 48)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Style</label>
            <select
              value={selectedBlock.content.style || 'filled'}
              onChange={(e) => updateBlockProperty('content.style', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="filled">Filled</option>
              <option value="outline">Outline</option>
            </select>
          </div>
        </PropertySection>
      )}

      {selectedBlock.type === 'table' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Table" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Data Source Description</label>
            <IsolatedInput
              value={selectedBlock.content.dataSourceDescription || ''}
              onChange={(value) => updateBlockProperty('content.dataSourceDescription', value)}
              placeholder="e.g., 12 months contracts, site details, offer comparison"
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            />
            <p className="text-xs text-gray-500 mt-1">
              Describe what data this table should contain for AI processing
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Rows</label>
              <input
                type="number"
                min="1"
                max="20"
                value={selectedBlock.content.rows || 3}
                onChange={(e) => updateBlockProperty('content.rows', parseInt(e.target.value) || 3)}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Columns</label>
              <input
                type="number"
                min="1"
                max="10"
                value={selectedBlock.content.cols || 3}
                onChange={(e) => updateBlockProperty('content.cols', parseInt(e.target.value) || 3)}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              />
            </div>
          </div>
        </PropertySection>
      )}

      {/* Bar Chart Properties */}
      {selectedBlock.type === 'bar-chart' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Chart Data" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Chart Title</label>
            <StableInput
              value={selectedBlock.content.title || ''}
              onChange={(value) => updateBlockProperty('content.title', value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="Chart Title"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Chart Data (JSON Format)</label>
            <StableTextarea
              value={JSON.stringify(selectedBlock.content.data || {
                labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
                datasets: [{
                  label: 'Sales',
                  data: [12, 19, 3, 5, 2, 3],
                  backgroundColor: '#3b82f6',
                  borderColor: '#1e40af',
                  borderWidth: 1
                }]
              }, null, 2)}
              onChange={(value) => {
                try {
                  const data = JSON.parse(value);
                  updateBlockProperty('content.data', data);
                } catch (error) {
                  // Invalid JSON, ignore
                }
              }}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none font-mono"
              rows={8}
              placeholder='{"labels": ["Jan", "Feb", "Mar"], "datasets": [{"label": "Sales", "data": [12, 19, 3]}]}'
            />
          </div>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectedBlock.content.showLegend !== false}
                onChange={(e) => updateBlockProperty('content.showLegend', e.target.checked)}
                className="mr-2"
              />
              <span className="text-xs">Show Legend</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectedBlock.content.showGrid !== false}
                onChange={(e) => updateBlockProperty('content.showGrid', e.target.checked)}
                className="mr-2"
              />
              <span className="text-xs">Show Grid</span>
            </label>
          </div>
        </PropertySection>
      )}

      {/* Line Chart Properties */}
      {selectedBlock.type === 'line-chart' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Chart Data" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Chart Title</label>
            <StableInput
              value={selectedBlock.content.title || ''}
              onChange={(value) => updateBlockProperty('content.title', value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="Chart Title"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Chart Data (JSON Format)</label>
            <StableTextarea
              value={JSON.stringify(selectedBlock.content.data || {
                labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
                datasets: [{
                  label: 'Revenue',
                  data: [65, 59, 80, 81, 56, 55],
                  backgroundColor: 'rgba(34, 197, 94, 0.2)',
                  borderColor: '#22c55e',
                  borderWidth: 2,
                  fill: true
                }]
              }, null, 2)}
              onChange={(value) => {
                try {
                  const data = JSON.parse(value);
                  updateBlockProperty('content.data', data);
                } catch (error) {
                  // Invalid JSON, ignore
                }
              }}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none font-mono"
              rows={8}
              placeholder='{"labels": ["Jan", "Feb", "Mar"], "datasets": [{"label": "Revenue", "data": [65, 59, 80]}]}'
            />
          </div>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectedBlock.content.showLegend !== false}
                onChange={(e) => updateBlockProperty('content.showLegend', e.target.checked)}
                className="mr-2"
              />
              <span className="text-xs">Show Legend</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectedBlock.content.showGrid !== false}
                onChange={(e) => updateBlockProperty('content.showGrid', e.target.checked)}
                className="mr-2"
              />
              <span className="text-xs">Show Grid</span>
            </label>
          </div>
        </PropertySection>
      )}

      {/* Pie Chart Properties */}
      {selectedBlock.type === 'pie-chart' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Chart Data" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Chart Title</label>
            <StableInput
              value={selectedBlock.content.title || ''}
              onChange={(value) => updateBlockProperty('content.title', value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="Chart Title"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Chart Data (JSON Format)</label>
            <StableTextarea
              value={JSON.stringify(selectedBlock.content.data || {
                labels: ['Desktop', 'Mobile', 'Tablet'],
                datasets: [{
                  label: 'Traffic Sources',
                  data: [55, 35, 10],
                  backgroundColor: ['#3b82f6', '#10b981', '#f59e0b'],
                  borderColor: ['#1e40af', '#059669', '#d97706'],
                  borderWidth: 2
                }]
              }, null, 2)}
              onChange={(value) => {
                try {
                  const data = JSON.parse(value);
                  updateBlockProperty('content.data', data);
                } catch (error) {
                  // Invalid JSON, ignore
                }
              }}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none font-mono"
              rows={8}
              placeholder='{"labels": ["Desktop", "Mobile", "Tablet"], "datasets": [{"label": "Traffic", "data": [55, 35, 10]}]}'
            />
          </div>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectedBlock.content.showLegend !== false}
                onChange={(e) => updateBlockProperty('content.showLegend', e.target.checked)}
                className="mr-2"
              />
              <span className="text-xs">Show Legend</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectedBlock.content.donut === true}
                onChange={(e) => updateBlockProperty('content.donut', e.target.checked)}
                className="mr-2"
              />
              <span className="text-xs">Donut Style</span>
            </label>
          </div>
        </PropertySection>
      )}

      {/* KPI Card Properties */}
      {selectedBlock.type === 'kpi-card' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="KPI Data" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
            <StableInput
              value={selectedBlock.content.title || ''}
              onChange={(value) => updateBlockProperty('content.title', value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="Total Revenue"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Value</label>
            <StableInput
              value={selectedBlock.content.value || ''}
              onChange={(value) => updateBlockProperty('content.value', value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="$125,430"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Subtitle</label>
            <StableInput
              value={selectedBlock.content.subtitle || ''}
              onChange={(value) => updateBlockProperty('content.subtitle', value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="This month"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Trend</label>
            <select
              value={selectedBlock.content.trend || 'neutral'}
              onChange={(e) => updateBlockProperty('content.trend', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="up">Up ↗</option>
              <option value="down">Down ↘</option>
              <option value="neutral">Neutral →</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Trend Value</label>
            <StableInput
              value={selectedBlock.content.trendValue || ''}
              onChange={(value) => updateBlockProperty('content.trendValue', value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="+12.5%"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
            <input
              type="color"
              value={selectedBlock.content.color || '#22c55e'}
              onChange={(e) => updateBlockProperty('content.color', e.target.value)}
              className="w-full h-8 border border-gray-300 rounded"
            />
          </div>
        </PropertySection>
      )}

      {/* Progress Bar Properties */}
      {selectedBlock.type === 'progress-bar' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Progress Data" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Label</label>
            <input
              type="text"
              value={selectedBlock.content.label || ''}
              onChange={(e) => updateBlockProperty('content.label', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="Project Completion"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Current Value</label>
            <input
              type="number"
              value={selectedBlock.content.value || 0}
              onChange={(e) => updateBlockProperty('content.value', parseFloat(e.target.value) || 0)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              min="0"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Maximum Value</label>
            <input
              type="number"
              value={selectedBlock.content.max || 100}
              onChange={(e) => updateBlockProperty('content.max', parseFloat(e.target.value) || 100)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              min="1"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Bar Height (px)</label>
            <input
              type="number"
              value={selectedBlock.content.height || 20}
              onChange={(e) => updateBlockProperty('content.height', parseInt(e.target.value) || 20)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              min="10"
              max="50"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
            <input
              type="color"
              value={selectedBlock.content.color || '#3b82f6'}
              onChange={(e) => updateBlockProperty('content.color', e.target.value)}
              className="w-full h-8 border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectedBlock.content.showPercentage !== false}
                onChange={(e) => updateBlockProperty('content.showPercentage', e.target.checked)}
                className="mr-2"
              />
              <span className="text-xs">Show Percentage</span>
            </label>
          </div>
        </PropertySection>
      )}

      {/* Gauge Chart Properties */}
      {selectedBlock.type === 'gauge-chart' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Gauge Data" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={selectedBlock.content.title || ''}
              onChange={(e) => updateBlockProperty('content.title', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="Performance"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Current Value</label>
            <input
              type="number"
              value={selectedBlock.content.value || 0}
              onChange={(e) => updateBlockProperty('content.value', parseFloat(e.target.value) || 0)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Minimum Value</label>
            <input
              type="number"
              value={selectedBlock.content.min || 0}
              onChange={(e) => updateBlockProperty('content.min', parseFloat(e.target.value) || 0)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Maximum Value</label>
            <input
              type="number"
              value={selectedBlock.content.max || 100}
              onChange={(e) => updateBlockProperty('content.max', parseFloat(e.target.value) || 100)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Unit</label>
            <input
              type="text"
              value={selectedBlock.content.unit || ''}
              onChange={(e) => updateBlockProperty('content.unit', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="%"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
            <input
              type="color"
              value={selectedBlock.content.color || '#22c55e'}
              onChange={(e) => updateBlockProperty('content.color', e.target.value)}
              className="w-full h-8 border border-gray-300 rounded"
            />
          </div>
        </PropertySection>
      )}

      {/* Badge Properties */}
      {selectedBlock.type === 'badge' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Badge Content" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Text</label>
            <input
              type="text"
              value={selectedBlock.content.text || ''}
              onChange={(e) => updateBlockProperty('content.text', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="New"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Variant</label>
            <select
              value={selectedBlock.content.variant || 'primary'}
              onChange={(e) => updateBlockProperty('content.variant', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="primary">Primary (Blue)</option>
              <option value="secondary">Secondary (Gray)</option>
              <option value="success">Success (Green)</option>
              <option value="warning">Warning (Yellow)</option>
              <option value="danger">Danger (Red)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Size</label>
            <select
              value={selectedBlock.content.size || 'medium'}
              onChange={(e) => updateBlockProperty('content.size', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </div>
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectedBlock.content.rounded !== false}
                onChange={(e) => updateBlockProperty('content.rounded', e.target.checked)}
                className="mr-2"
              />
              <span className="text-xs">Rounded</span>
            </label>
          </div>
        </PropertySection>
      )}

      {/* Quote Properties */}
      {selectedBlock.type === 'quote' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Quote Content" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Quote Text</label>
            <StableTextarea
              value={selectedBlock.content.text || ''}
              onChange={(value) => updateBlockProperty('content.text', value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none"
              rows={3}
              placeholder="The best way to predict the future is to create it."
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Author</label>
            <StableInput
              value={selectedBlock.content.author || ''}
              onChange={(value) => updateBlockProperty('content.author', value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="Peter Drucker"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Citation</label>
            <StableInput
              value={selectedBlock.content.citation || ''}
              onChange={(value) => updateBlockProperty('content.citation', value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="Management Consultant"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Style</label>
            <select
              value={selectedBlock.content.style || 'blockquote'}
              onChange={(e) => updateBlockProperty('content.style', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="blockquote">Blockquote</option>
              <option value="modern">Modern</option>
              <option value="minimal">Minimal</option>
            </select>
          </div>
        </PropertySection>
      )}

      {/* Callout Properties */}
      {selectedBlock.type === 'callout' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Callout Content" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
            <StableInput
              type="text"
              value={selectedBlock.content.title || ''}
              onChange={(value) => updateBlockProperty('content.title', value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="Important Note"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Text</label>
            <StableTextarea
              value={selectedBlock.content.text || ''}
              onChange={(value) => updateBlockProperty('content.text', value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none"
              rows={3}
              placeholder="This is an important message that needs attention."
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
            <select
              value={selectedBlock.content.type || 'info'}
              onChange={(e) => updateBlockProperty('content.type', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="info">Info (Blue)</option>
              <option value="warning">Warning (Yellow)</option>
              <option value="success">Success (Green)</option>
              <option value="error">Error (Red)</option>
            </select>
          </div>
        </PropertySection>
      )}

      {/* Data Table Properties */}
      {selectedBlock.type === 'data-table' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Table Data" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Data Source Description</label>
            <IsolatedInput
              value={selectedBlock.content.dataSourceDescription || ''}
              onChange={(value) => updateBlockProperty('content.dataSourceDescription', value)}
              placeholder="e.g., 12 months contracts, site details, offer comparison"
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            />
            <p className="text-xs text-gray-500 mt-1">
              Describe what data this table should contain for AI processing
            </p>
          </div>
          <div className="mt-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">Headers (comma-separated)</label>
            <input
              type="text"
              value={selectedBlock.content.headers?.join(', ') || ''}
              onChange={(e) => {
                const headers = e.target.value.split(',').map(h => h.trim()).filter(h => h);
                updateBlockProperty('content.headers', headers);
              }}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="Name, Role, Department, Salary"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Table Data (JSON format)</label>
            <StableTextarea
              value={JSON.stringify(selectedBlock.content.rows || [], null, 2)}
              onChange={(value) => {
                try {
                  const rows = JSON.parse(value);
                  updateBlockProperty('content.rows', rows);
                } catch (error) {
                  // Invalid JSON, ignore
                }
              }}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none font-mono"
              rows={6}
              placeholder='[["John Doe", "Manager", "Sales", "$75,000"]]'
            />
          </div>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectedBlock.content.striped !== false}
                onChange={(e) => updateBlockProperty('content.striped', e.target.checked)}
                className="mr-2"
              />
              <span className="text-xs">Striped Rows</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectedBlock.content.sortable === true}
                onChange={(e) => updateBlockProperty('content.sortable', e.target.checked)}
                className="mr-2"
              />
              <span className="text-xs">Sortable</span>
            </label>
          </div>

          {/* Advanced Controls Button */}
          <div className="mt-3 pt-3 border-t border-gray-200">
            <button
              onClick={() => setShowAdvancedDataTableControls(true)}
              className="w-full px-3 py-2 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 flex items-center justify-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Advanced Table Controls
            </button>
          </div>
        </PropertySection>
      )}

      {/* Border Frame Properties */}
      {selectedBlock.type === 'border-frame' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Frame Style" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Border Style</label>
            <select
              value={selectedBlock.content.borderStyle || 'solid'}
              onChange={(e) => updateBlockProperty('content.borderStyle', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
              <option value="double">Double</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Border Width (px)</label>
            <input
              type="number"
              value={selectedBlock.content.borderWidth || 2}
              onChange={(e) => updateBlockProperty('content.borderWidth', parseInt(e.target.value) || 2)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              min="1"
              max="10"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Border Color</label>
            <input
              type="color"
              value={selectedBlock.content.borderColor || '#3b82f6'}
              onChange={(e) => updateBlockProperty('content.borderColor', e.target.value)}
              className="w-full h-8 border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Border Radius (px)</label>
            <input
              type="number"
              value={selectedBlock.content.borderRadius || 8}
              onChange={(e) => updateBlockProperty('content.borderRadius', parseInt(e.target.value) || 8)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              min="0"
              max="50"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Padding (px)</label>
            <input
              type="number"
              value={selectedBlock.content.padding || 16}
              onChange={(e) => updateBlockProperty('content.padding', parseInt(e.target.value) || 16)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              min="0"
              max="50"
            />
          </div>
        </PropertySection>
      )}

      {/* Icon Block Properties */}
      {selectedBlock.type === 'icon-block' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Icon Settings" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Icon Name</label>
            <select
              value={selectedBlock.content.iconName || 'Zap'}
              onChange={(e) => updateBlockProperty('content.iconName', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="Zap">Zap ⚡</option>
              <option value="Star">Star ⭐</option>
              <option value="Heart">Heart ❤️</option>
              <option value="CheckCircle">Check Circle ✅</option>
              <option value="AlertCircle">Alert Circle ⚠️</option>
              <option value="Info">Info ℹ️</option>
              <option value="Settings">Settings ⚙️</option>
              <option value="User">User 👤</option>
              <option value="Mail">Mail 📧</option>
              <option value="Phone">Phone 📞</option>
              <option value="Calendar">Calendar 📅</option>
              <option value="Clock">Clock 🕐</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Size (px)</label>
            <input
              type="number"
              value={selectedBlock.content.size || 48}
              onChange={(e) => updateBlockProperty('content.size', parseInt(e.target.value) || 48)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              min="16"
              max="128"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
            <input
              type="color"
              value={selectedBlock.content.color || '#3b82f6'}
              onChange={(e) => updateBlockProperty('content.color', e.target.value)}
              className="w-full h-8 border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Style</label>
            <select
              value={selectedBlock.content.style || 'outline'}
              onChange={(e) => updateBlockProperty('content.style', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="outline">Outline</option>
              <option value="filled">Filled</option>
            </select>
          </div>
        </PropertySection>
      )}

      {/* Timeline Properties */}
      {selectedBlock.type === 'timeline' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Timeline Items" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Timeline Data (JSON Format)</label>
            <StableTextarea
              value={JSON.stringify(selectedBlock.content.items || [
                { id: '1', title: 'Project Started', description: 'Initial planning phase', date: '2024-01-01', status: 'completed' },
                { id: '2', title: 'Development', description: 'Core development work', date: '2024-02-01', status: 'current' },
                { id: '3', title: 'Testing', description: 'Quality assurance', date: '2024-03-01', status: 'upcoming' }
              ], null, 2)}
              onChange={(value) => {
                try {
                  const items = JSON.parse(value);
                  updateBlockProperty('content.items', items);
                } catch (error) {
                  // Invalid JSON, ignore
                }
              }}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none font-mono"
              rows={8}
              placeholder='[{"id": "1", "title": "Task 1", "description": "Description", "date": "2024-01-01", "status": "completed"}]'
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Orientation</label>
            <select
              value={selectedBlock.content.orientation || 'vertical'}
              onChange={(e) => updateBlockProperty('content.orientation', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="vertical">Vertical</option>
              <option value="horizontal">Horizontal</option>
            </select>
          </div>
        </PropertySection>
      )}

      {/* Accordion Properties */}
      {selectedBlock.type === 'accordion' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Accordion Items" icon={Type} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Accordion Data (JSON Format)</label>
            <StableTextarea
              value={JSON.stringify(selectedBlock.content.items || [
                { id: '1', title: 'Section 1', content: 'Content for section 1', isOpen: true },
                { id: '2', title: 'Section 2', content: 'Content for section 2', isOpen: false },
                { id: '3', title: 'Section 3', content: 'Content for section 3', isOpen: false }
              ], null, 2)}
              onChange={(value) => {
                try {
                  const items = JSON.parse(value);
                  updateBlockProperty('content.items', items);
                } catch (error) {
                  // Invalid JSON, ignore
                }
              }}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none font-mono"
              rows={8}
              placeholder='[{"id": "1", "title": "Section 1", "content": "Content here", "isOpen": true}]'
            />
          </div>
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectedBlock.content.allowMultiple !== false}
                onChange={(e) => updateBlockProperty('content.allowMultiple', e.target.checked)}
                className="mr-2"
              />
              <span className="text-xs">Allow Multiple Open</span>
            </label>
          </div>
        </PropertySection>
      )}

      {/* Script Block Properties */}
      {selectedBlock.type === 'script' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Script Content" icon={Code} sectionKey="content">
          <ScriptEditor
            blockId={selectedBlock.id}
            content={selectedBlock.content}
            onUpdate={updateBlockProperty}
          />
        </PropertySection>
      )}

      {selectedBlock.type === 'section-marker' && (
        <PropertySection expandedSections={expandedSections} onToggle={toggleSection} title="Section" icon={Layers} sectionKey="content">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Section Name</label>
            <StableInput
              key={`section-name-${selectedBlock.id}`}
              value={selectedBlock.content?.title || ''}
              onChange={(val) => updateBlockProperty('content.title', val)}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              placeholder="Enter section name…"
            />
            <p className="mt-1 text-xs text-gray-400">
              This label is invisible in the document — it helps organise sections for AI regeneration.
            </p>
          </div>
        </PropertySection>
      )}

      {/* Actions */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <button
          onClick={() => onDeleteBlock(selectedBlock.id)}
          className="w-full flex items-center justify-center px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete Block
        </button>
      </div>

      {/* Advanced Data Table Controls Modal */}
      {showAdvancedDataTableControls && selectedBlock?.type === 'data-table' && (
        <AdvancedDataTableControls
          block={selectedBlock}
          onUpdateBlock={onUpdateBlock}
          onClose={() => setShowAdvancedDataTableControls(false)}
        />
      )}
    </div>
  );
});

PropertyPanel.displayName = 'PropertyPanel';

export default PropertyPanel;
